import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import {
    DEPTH,
    ORDER_TYPE,
    POSITION_SIDE,
    PRICE_INCREMENT_DECREMENT_VALUE,
    PRICE_TYPE,
    TIME_IN_FORCE,
    TRADE_TYPE,
} from 'src/common/constants';
import { MAX_INPUT } from 'src/common/constants';
import { currencyFormatter, roundNumber } from 'src/common/helpers';
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Input,
    Range,
    Checkbox,
} from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { createOrder } from 'src/setup/redux/slices/derivativesSlice';
import { FutureOrderPayload, Position } from 'src/types';

interface PositionLimitCloseModalProps {
    open: boolean;
    lastPrice: number;
    position: Position;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { colors, typography, breakpoints } }) => `
        .modal-paper {
            border-radius: 8px;
            @media (min-width: ${breakpoints.md}) {
                border-radius: 10px;
                width: 447px;
            }
        }
        .modal-title {
            padding: 20px 24px 0;
            > div {
                font-size: ${typography.size.desktop.paragraph.p1};
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                @media (max-width: ${breakpoints.md}) {
                    font-size: ${typography.size.desktop.paragraph.p2};
                }
            }
        }
        .modal-content {
            padding: 32px 24px 24px;
            .price-input {
                margin-bottom: 16px
            }
            .quantity-input {
                margin-bottom: 8px;
                padding: 0 12px 0 0;
                input {
                    font-size: ${typography.size.desktop.paragraph.p2};
                    line-height: calc(${typography.lineHeight[3]});
                    padding: 11px 12px;
                }
                &.input-element-error {
                    border-color: ${colors.border.light};    
                }
            }
            .value-up-down {
                span {
                    &:nth-of-type(1) {
                        padding-right: 10px;
                    }
                    &:nth-of-type(3) {
                        padding-left: 10px;
                    }
                }
            }
        }
        .modal-actions {
            padding: 24px 24px 30px;
            gap: 16px;
            button {
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                padding: 10px 68px;
                &:nth-of-type(2) {
                    color: ${colors.text.tertiary};
                }
            }
            @media (max-width: 425px) {
                justify-content: center;
                button {
                    padding: 8px 46px;
                }
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        color: ${colors.text.secondary};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        margin-bottom: 8px;
    `}
`;

const TitleText = styled.div`
    display: inline-block;
`;

const StyledSpan = styled.span`
    ${({ color }) => `
        color: ${color};
        cursor: pointer;
    `}
`;

const EstimatedPLText = styled.div`
    ${({ theme: { colors, typography, breakpoints } }) => `
        font-size: ${typography.size.desktop.paragraph.p2};
        color: ${colors.text.secondary};
        line-height: ${typography.lineHeight[5]};
        @media (max-width: ${breakpoints.md}) {
            font-size: ${typography.size.desktop.paragraph.p3}; 
            width: 50%;
        }
    `}
`;

const EstimatedPLValue = styled.div<{ color?: string }>`
    ${({ theme: { colors, typography, breakpoints }, color }) => `
        font-size: ${typography.size.desktop.paragraph.p2}; 
        color: ${color ? color : colors.text.primary};
        line-height: ${typography.lineHeight[4]};
        text-align: right;
        @media (max-width: ${breakpoints.md}) {
            font-size: ${typography.size.desktop.paragraph.p3}; 
            width: 50%;
        }
    `}
`;

const PositionLimitCloseModal: FC<PositionLimitCloseModalProps> = ({
    open,
    lastPrice,
    position,
    handleClose,
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const originalQuantity = position.available;
    const [price, setPrice] = useState(lastPrice.toString());
    const [quantity, setQuantity] = useState(originalQuantity.toString());
    const [quantityPercentage, setQuantityPercentage] = useState(100);
    const [estimatedPnL, setEstimatedPnl] = useState(0);
    const [postOnly, setPostOnly] = useState(false);
    const [quantityError, setQuantityError] = useState('');
    const futuresSymbol = useAppSelector((state) => state.global.configV2.futuresSymbol);
    const currentSymbol = futuresSymbol[position.symbolId];
    const createOrderLoading = useAppSelector((state) => state.derivatives.loading.createOrder);
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const maxTradeQuantityDecimals = DEPTH[currentSymbol.minTradeQuantity];
    const maxPriceDecimals = DEPTH[currentSymbol?.minPricePrecision];
    // regular expression to validate price
    const priceRegEx = new RegExp(
        `^[0-9]*${
            maxPriceDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxPriceDecimals + '})?$'
                : '?$'
        }`,
    );

    // regular expression to validate quantity
    const quantityRegEx = new RegExp(
        `^[0-9]*${
            maxTradeQuantityDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxTradeQuantityDecimals + '})?$'
                : '?$'
        }`,
    );

    const dispatch = useAppDispatch();

    const handleConfirm = () => {
        if (Number(quantity) < Number(currentSymbol.minTradeQuantity)) {
            setQuantityError(
                `Close quantity cannot be less than ${currentSymbol.minTradeQuantity}`,
            );
            return;
        }
        if (Number(quantity) > originalQuantity) {
            setQuantityError(`Close quantity cannot be more than ${originalQuantity}`);
            return;
        }
        const closeOrder: FutureOrderPayload = {
            price_type: PRICE_TYPE.LIMIT,
            price: String(price),
            quantity: String(quantity),
            symbol_id: position.symbolId,
            client_order_id: new Date().getTime(),
            exchange_id: position.exchangeId,
            side:
                position.isLong === POSITION_SIDE.LONG
                    ? TRADE_TYPE.SELL_CLOSE
                    : TRADE_TYPE.BUY_CLOSE,
            time_in_force: postOnly ? TIME_IN_FORCE.MAKER : TIME_IN_FORCE.GTC,
            order_side: position.positionId,
            type: ORDER_TYPE.LIMIT,
            futures: true,
            account_index: userCurrentFutureTradeAccountIndex,
        };
        dispatch(createOrder(closeOrder));
        handleClose();
    };

    const handleChange = (
        value: string | number | number[] | boolean,
        name: string | undefined,
    ) => {
        //don't change when change is not according to Regex or greater than max input
        if (
            (name === 'quantity' && !quantityRegEx.test(value.toString())) ||
            (name === 'price' && !priceRegEx.test(value.toString())) ||
            Number(value) > MAX_INPUT
        ) {
            return;
        }

        // if value is . append 0 before .
        value = value === '.' ? '0.' : value;
        setQuantityError('');

        if (name == 'price') {
            setPrice(value.toString());
        } else if (name === 'quantity') {
            setQuantity(value.toString());
            let currentPercentage = (Number(value) * 100) / originalQuantity;
            if (currentPercentage > 100) {
                currentPercentage = 100;
            }
            setQuantityPercentage(Number(currentPercentage.toFixed(2)));
        } else if (name === 'quantityPercentage') {
            const currentQuantity = (Number(value) * originalQuantity) / 100;
            setQuantity(roundNumber(currentQuantity, maxTradeQuantityDecimals).toString());
            setQuantityPercentage(Number(value));
        }
    };

    useEffect(() => {
        const entryPrice = position.avgPrice;
        const entryPricePositionValue = entryPrice * Number(quantity);
        const closePricePositionValue = Number(quantity) * Number(price);
        const estimatedProfit =
            position.isLong === POSITION_SIDE.LONG
                ? closePricePositionValue - entryPricePositionValue
                : entryPricePositionValue - closePricePositionValue;
        setEstimatedPnl(estimatedProfit);
    }, [quantity, price, position]);

    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>{t('limit_close')}</TitleText>
                <Cross
                    fill={colors.text.secondary}
                    onClick={handleClose}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <FlexContainer
                    styles={{ flexDirection: 'column', width: '100%', alignItems: 'start' }}
                >
                    <LabelText>{`${t('closed_price')} ${currentSymbol.quoteTokenId}`}</LabelText>
                    <Input
                        name={'price'}
                        hasTooltip={false}
                        fullWidth
                        value={price}
                        className={'price-input'}
                        onChange={handleChange}
                        rightElement={
                            <FlexContainer
                                className="value-up-down"
                                styles={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '39px',
                                }}
                            >
                                <StyledSpan
                                    onClick={() => {
                                        handleChange(
                                            Number(price) - PRICE_INCREMENT_DECREMENT_VALUE,
                                            'price',
                                        );
                                    }}
                                >
                                    -
                                </StyledSpan>
                                <StyledSpan color={colors.text.tertiary}>|</StyledSpan>
                                <StyledSpan
                                    onClick={() => {
                                        handleChange(
                                            Number(price) + PRICE_INCREMENT_DECREMENT_VALUE,
                                            'price',
                                        );
                                    }}
                                >
                                    +
                                </StyledSpan>
                            </FlexContainer>
                        }
                    />
                    <LabelText>
                        {`${t('closed_qty')} ${currentSymbol.secondLevelUnderlyingId}`}
                    </LabelText>
                    <Input
                        name={'quantity'}
                        hasTooltip={false}
                        fullWidth
                        value={quantity}
                        className={'quantity-input'}
                        onChange={handleChange}
                        error={quantityError}
                        rightElement={
                            <FlexContainer
                                className="value-up-down"
                                styles={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '39px',
                                }}
                            >
                                <StyledSpan
                                    onClick={() => {
                                        let dprice = roundNumber(
                                            (Number(quantity) * 1000 -
                                                Number(currentSymbol.minTradeQuantity) * 1000) /
                                                1000,
                                            maxTradeQuantityDecimals,
                                        );
                                        handleChange(dprice, 'quantity');
                                    }}
                                >
                                    -
                                </StyledSpan>
                                <StyledSpan color={colors.text.tertiary}>|</StyledSpan>
                                <StyledSpan
                                    onClick={() => {
                                        let dprice = roundNumber(
                                            (Number(quantity) * 1000 +
                                                Number(currentSymbol.minTradeQuantity) * 1000) /
                                                1000,
                                            maxTradeQuantityDecimals,
                                        );
                                        handleChange(dprice, 'quantity');
                                    }}
                                >
                                    +
                                </StyledSpan>
                            </FlexContainer>
                        }
                    />
                    <Range
                        name="quantityPercentage"
                        value={quantityPercentage}
                        onChange={handleChange}
                    />
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 16,
                            marginTop: '32px',
                        }}
                    >
                        <EstimatedPLText>{t('estimated_pnl')}</EstimatedPLText>
                        <EstimatedPLValue
                            color={
                                estimatedPnL === 0
                                    ? colors.text.primary
                                    : estimatedPnL > 0
                                    ? colors.alert.green
                                    : colors.alert.red
                            }
                        >
                            {estimatedPnL > 0 && '+'}
                            {currencyFormatter(
                                estimatedPnL,
                                DEPTH[currentSymbol?.baseTokenFutures?.marginPrecision],
                            )}{' '}
                            {currentSymbol.quoteTokenId}
                        </EstimatedPLValue>
                    </FlexContainer>
                    <FlexContainer styles={{ marginTop: '16px' }}>
                        <Checkbox
                            label={t('post_only')}
                            name={'postOnly'}
                            value={postOnly}
                            onChange={setPostOnly}
                        />
                    </FlexContainer>
                </FlexContainer>
            </ModalContent>
            <ModalActions>
                <Button
                    loading={createOrderLoading}
                    onClick={handleConfirm}
                    disabled={quantityError !== ''}
                >
                    {t('confirm')}
                </Button>
                <Button variant="outlined" onClick={handleClose}>
                    {t('cancel')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { PositionLimitCloseModal };
